import {
  Component,
  Input,
  ViewChild
} from '@angular/core';
import {
  ControlContainer,
  NgForm,
  NgModel
} from '@angular/forms';

import { BigDecimalService }    from '../../../core/services/big-decimal.service';
import { TextControlComponent } from '../text-control/text-control.component';

@Component({
  selector   : 'wor-counter-control',
  styles: [ require('./counter-control.component.scss') ],
  template: require('./counter-control.component.html'),

  viewProviders: [
    {
      provide    : ControlContainer,
      useExisting: NgForm
    }
  ]
})
export class CounterControlComponent extends TextControlComponent {
  // Lets the user type; on leaving, a value under one step counts in steps and anything else rounds up to a whole step.
  @Input() isEditable  = false;
  @Input() max        ?: number | string;
  @Input() min         = 0;

  @ViewChild('input') input : NgModel;

  _round: number;
  _step : number | string;

  @Input() set round ( _round : number | string ) {
    this._round = _round && this.bigDecimalService.isNumber(_round) && +_round >= 0
      ? +_round
      : 0;
  }

  @Input() set step ( _step : number | string ) {
    this._step = _step
      ? this.bigDecimalService.get(_step, this._round).toString()
      : 1;
  }

  constructor (
    private bigDecimalService: BigDecimalService
  ) {
    super();
  }

  private _hasLimit ( limit : number | string ) : boolean {
    return limit !== undefined && limit !== null && limit !== '';
  }

  // A blank field reads as 0 and text that is not a number as the minimum.
  private _parseTyped ( value : string ) : number {
    const typed = parseInt(value, 10);

    if ( !isNaN(typed) ) {
      return typed;
    }

    return value === '' || !this._hasLimit(this.min)
      ? 0
      : +this.min;
  }

  private _setModel ( value : number ) : void {
    const model = this.bigDecimalService.get(value, this._round).toString();

    if ( model !== String(this._model) ) {
      this._model = model;
      this.modelChange.emit(this._model);
    }

    this._showModel();
  }

  // Repaints the input and its control even when the corrected value equals the bound one.
  private _showModel () : void {
    this.input?.control.setValue(this._model, { emitViewToModelChange: false });
  }

  onBlur ( event : FocusEvent ) : void {
    if ( this.isEditable ) {
      const step  = +this._step || 1;
      const typed = this._parseTyped(this._model);

      let value = typed < step
        ? typed * step
        : Math.ceil(typed / step) * step;

      if ( this._hasLimit(this.min) && value < +this.min ) {
        value = +this.min;
      }

      if ( this._hasLimit(this.max) && value > +this.max ) {
        value = +this.max;
      }

      this._setModel(value);
    }

    this.blur.emit(event);
  }

  onModelChange ( value : string ) : void {
    if ( !this.isEditable ) {
      this._model = value;
      this.modelChange.emit(this._model);

      return;
    }

    const previous = String(this._model);

    this._model = String(this._parseTyped(value));

    if ( this._model !== value ) {
      this._showModel();
    }

    if ( this._model !== previous ) {
      this.modelChange.emit(this._model);
    }
  }

  onStepClick ( direction = 'up' ) : void {
    const limit = direction === 'down'
      ? this.min
      : this.max;

    const roundedLimit = direction === 'down'
      ? this.bigDecimalService.get(this.min, this._round)
      : this.bigDecimalService.get(this.max, this._round);

    const step = this.bigDecimalService.get(this._step, this._round);

    let model = this.bigDecimalService.get(this._model, this._round);

    const next = direction === 'down'
      ? model.subtract(step)
      : model.add(step);

    model = ( limit !== undefined && limit !== null )
      && ( ( direction === 'down' && ( model <= roundedLimit || next <= roundedLimit ) )
      || ( direction === 'up' && ( model >= roundedLimit || next >= roundedLimit ) ) )
        ? roundedLimit
        : next;

    this._model = this.bigDecimalService.get(model, this._round).toString();

    this.modelChange.emit(this._model);
  }
}
